import { useState } from "react";
import { api } from "@/lib/rn3";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Mail, Phone, Globe2, Send } from "lucide-react";
import SEO from "@/components/SEO";

const Contact = () => {
  const t = useT();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact.success"));
    } catch { toast.error("Failed to send"); }
  };
  return (
    <div className="bg-white">
      <SEO
        title="Contact Roman Numerals III — Jacksonville, FL | 904-776-8318"
        description="Contact Roman Numerals III in Jacksonville, FL. Call 904-776-8318, email info@romannumeralsiii.com, or visit our office at 8512 Tower Falls Drive."
        path="/contact"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "url": "https://romannumeralsiii.com/contact",
          "mainEntity": { "@id": "https://romannumeralsiii.com/#localbusiness" }
        }}
      />
      <section className="bg-[#FDFBF7]">
        <div className="rn3-container py-16">
          <div className="rn3-eyebrow">{t("contact.eyebrow")}</div>
          <h1 className="font-serif-display text-5xl mt-3">{t("contact.h1_a")} <span className="italic text-[#8B1C22]">{t("contact.h1_b")}</span></h1>
          <p className="mt-4 text-[#404040] max-w-xl">{t("contact.intro")}</p>
        </div>
      </section>
      <section className="rn3-section">
        <div className="rn3-container grid lg:grid-cols-2 gap-12">
          {sent ? (
            <div className="rn3-card" data-testid="contact-thanks">
              <h2 className="font-serif-display text-3xl">{t("contact.success")}</h2>
              <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div><label className="rn3-eyebrow block mb-2">{t("common.full_name")}</label><input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rn3-input" data-testid="contact-name" /></div>
                <div><label className="rn3-eyebrow block mb-2">{t("common.email")}</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="contact-email" /></div>
                <div><label className="rn3-eyebrow block mb-2">{t("common.phone")}</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="contact-phone" /></div>
                <div><label className="rn3-eyebrow block mb-2">{t("contact.form.subject")}</label><input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rn3-input" data-testid="contact-subject" /></div>
                <div className="sm:col-span-2"><label className="rn3-eyebrow block mb-2">{t("contact.form.message")}</label><textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rn3-input" rows="5" data-testid="contact-message" /></div>
              </div>
              <button type="submit" className="rn3-btn-primary" data-testid="contact-submit"><Send className="w-4 h-4" /> {t("contact.form.send")}</button>
            </form>
          )}
          <aside className="rn3-card bg-[#FDFBF7]">
            <h3 className="font-serif-display text-2xl">{t("footer.contact")}</h3>
            <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
            <div className="space-y-4 text-sm text-[#404040]">
              <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#C4A252]" /> info@romannumeralsiii.com</div>
              <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#C4A252]" /> {t("footer.call_to_schedule")}</div>
              <div className="flex items-center gap-3"><Globe2 className="w-4 h-4 text-[#C4A252]" /> {t("contact.detail.languages")} · {t("contact.detail.languages_val")}</div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
